<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { SplitterGroup, SplitterPanel, SplitterResizeHandle } from "reka-ui";

import CaseSearchList from "@/components/CaseSearch/CaseSearchList.vue";
import CaseSearchForm from "@/components/CaseSearch/CaseSearchForm.vue";

import { useBrandStore } from "@/stores";

import { getDefaultCaseSortOrder } from "@/services/UtilitiesService";

import { useRootLayout } from "@/hooks/use-root-layout";
import { ODATA_FILTER_GROUP_NAME } from "@/models/CaseSearchResult";

import type { Column } from "@/models/Column";
import type { PagedData } from "@/models/PagedData";
import type { CaseSearchPersistedFilter, CaseSearchResult } from "@/models/CaseSearchResult";

const { layout } = useRootLayout();

const currentError = ref<string>();
const isLoading = ref(false);
const openSearchPanel = ref(false);

const brand = computed(() => useBrandStore().selectedBrand);
const isFixedLayout = computed(() => layout.value === "FIXED");
const activeLens = computed(() => useBrandStore().selectedLens.code);

// Session Storage
const brandPageStorageKey = computed(() => `CASE_SEARCH_PAGE-${brand.value}/${activeLens.value}`);
const brandPageSizeStorageKey = computed(() => `CASE_SEARCH_PAGE_SIZE-${brand.value}/${activeLens.value}`);
const brandFormDataStorageKey = computed(() => `CASE_SEARCH_FORM_DATA-${brand.value}/${activeLens.value}`);
const brandSortOrderStorageKey = computed(() => `CASE_SEARCH_SORT_ORDER-${brand.value}/${activeLens.value}`);
// Local Storage
const brandHiddenColumnsStorageKey = computed(() => `CASE_SEARCH_HIDDEN_COLUMNS-${brand.value}/${activeLens.value}`);

const SearchedCasesPage = ref(
  (() => {
    const stored = sessionStorage.getItem(brandPageStorageKey.value);
    if (stored == null || isNaN(+stored)) return 1;
    return +stored;
  })(),
);
const SearchedCasesSize = ref(
  (() => {
    const stored = sessionStorage.getItem(brandPageSizeStorageKey.value);
    if (stored == null || isNaN(+stored)) return 25;
    return +stored;
  })(),
);
const searchedCases = ref<PagedData<CaseSearchResult[]>>({
  page: 1,
  items: [],
  totalCount: 0,
});

const caseSearchFormRef = ref<InstanceType<typeof CaseSearchForm>>();
const searchKey = ref("");
const sortedColumn = ref<SortOrder>(
  (() => {
    const stored = sessionStorage.getItem(brandSortOrderStorageKey.value);
    if (stored != null && stored !== "{}") return JSON.parse(stored) as SortOrder;
    return getDefaultCaseSortOrder() as SortOrder;
  })(),
);

watch(isFixedLayout, () => (openSearchPanel.value = false));

const changePagination: ChangePagination = ({ page, size }) => {
  if (page != null) SearchedCasesPage.value = page;
  if (size != null) SearchedCasesSize.value = size;
};

const toggleSort: ToggleSort = (order) => {
  sortedColumn.value = order;
  sessionStorage.setItem(brandSortOrderStorageKey.value, JSON.stringify(sortedColumn.value));
};

const onFilterDelete: DeleteFilter = (column, index) => {
  if (caseSearchFormRef.value == null) return;

  const stored = JSON.parse(sessionStorage.getItem(brandFormDataStorageKey.value) ?? "{}") as CaseSearchPersistedFilter;

  const toRemove = stored.rawFilters?.[column.filterConfig?.filterGroup ?? ODATA_FILTER_GROUP_NAME]?.[column.fieldPath];
  if (toRemove?.value == null) return;

  // Filter out the single array item
  if (Array.isArray(toRemove.value)) toRemove.value = toRemove.value.filter((_, idx) => idx !== index);
  // Reset the value to a string with a space (to serve the select fields empty value), which will be trimmed
  // to an empty string
  else if (typeof toRemove.value === "string" || typeof toRemove.value === "number") toRemove.value = " ";
  // Reset other
  else {
    toRemove.value.from = "";
    toRemove.value.to = "";
  }

  // The object updated is the same throughout, so should be the updated filter here
  caseSearchFormRef.value?.forceLastSearch(stored);
};

const storeSearchedCases = (data: PagedData<CaseSearchResult[]>, size: number) => {
  searchedCases.value = data;
  searchKey.value = Math.random().toString(36);
  sessionStorage.setItem(brandPageStorageKey.value, data.page.toString());
  sessionStorage.setItem(brandPageSizeStorageKey.value, size.toString());
  // auto-close form after search
  openSearchPanel.value = false;
};

function toggleSearchPanelOpen() {
  if (!isFixedLayout.value) return;
  openSearchPanel.value = !openSearchPanel.value;
}

export type SortOrder = { order: "asc" | "desc"; path: string };
export type ToggleSort = (order: { order: "asc" | "desc"; path: string }) => void;
export type ChangePagination = (data: Partial<Record<"page" | "size", number>>) => void;
export type DeleteFilter = (column: Column, index: number) => void;
</script>
<template>
  <button
    v-if="isFixedLayout"
    type="button"
    class="tw-btn-green tw-absolute tw-right-3 tw-top-3 tw-z-[100001] tw-aspect-square tw-p-3"
    :aria-pressed="openSearchPanel"
    @click="toggleSearchPanelOpen"
  >
    <span class="tw-sr-only">{{ openSearchPanel ? "Close" : "Open" }} Search Form</span>
    <i aria-hidden="true" class="fas fa-2xl" :class="openSearchPanel ? 'fa-xmark' : 'fa-filter'" />
  </button>
  <SplitterGroup direction="horizontal" auto-save-id="case-search-root-layout-split">
    <SplitterPanel
      as="aside"
      class="tw-bg-white"
      :min-size="isFixedLayout ? 0 : 25"
      :default-size="isFixedLayout ? 0 : 25"
      :aria-hidden="isFixedLayout && !openSearchPanel"
      :inert="isFixedLayout && !openSearchPanel ? -1 : 0"
      :class="
        isFixedLayout && [
          'tw-absolute tw-inset-0 tw-z-[100000] tw-size-full tw-transition-transform',
          !openSearchPanel && '-tw-translate-x-full',
        ]
      "
    >
      <CaseSearchForm
        ref="caseSearchFormRef"
        :key="brand"
        v-model:error="currentError"
        v-model:is-loading="isLoading"
        class="tw-h-full"
        :page="SearchedCasesPage"
        :page-size="SearchedCasesSize"
        :column-sort-direction="sortedColumn"
        :brand-form-data-storage-key="brandFormDataStorageKey"
        @search-callback="storeSearchedCases"
        @change-pagination="changePagination"
      />
    </SplitterPanel>
    <SplitterResizeHandle v-if="!isFixedLayout" class="tw-w-4" />
    <SplitterPanel
      as="main"
      aria-label="Case Search Results List"
      class="tw-flex tw-p-4 tw-shadow-inner"
      :min-size="isFixedLayout ? 100 : 50"
      :class="{ 'tw-pl-0': !isFixedLayout }"
    >
      <keep-alive>
        <CaseSearchList
          :sorted-column="sortedColumn"
          :page="SearchedCasesPage"
          :size="SearchedCasesSize"
          :search-key="searchKey"
          :result="searchedCases"
          :is-loading="isLoading"
          :current-error="currentError"
          :brand-hidden-columns-storage-key="brandHiddenColumnsStorageKey"
          :last-search="caseSearchFormRef?.lastSearch"
          @refresh-page="caseSearchFormRef?.refreshPage"
          @change-pagination="changePagination"
          @delete-filter="onFilterDelete"
          @toggle-sort="toggleSort"
        />
      </keep-alive>
    </SplitterPanel>
  </SplitterGroup>
  <router-view />
</template>
